import { constants } from '@cloudbase/utilities'
import { Camelize, FileBody, FileObject, FileObjectV2, FileOptions, TransformOptions } from './supabase/types'
import { Result } from './types'
import { isStorageError, StorageError } from './supabase/errors'
import { CloudbaseStorage, ICloudbaseContext, COMPONENT_NAME } from './cloudbase-storage'
import { toBase64 } from './encoding'

const { ERRORS } = constants

/**
 * 传统 CloudBase 对象操作客户端（Supabase-like 包装层，底层仍走传统云存储 HTTP API，即 `CloudbaseStorage` 的顶层方法）。
 *
 * 通过 `app.storage.from()`（不传 bucketId）获取，与 PG 原生的 `StorageFileApi` 对等但**通道不同**。
 *
 * 两环境下的行为：
 * - 传统环境：原生用法，`upload/download/remove` 走传统接口，path 为 COS Bucket 内相对路径。
 * - Neo 环境：因底层仍是传统兼容路径，**path 第一段同样会被服务端当作 PG `bucket_id`**；
 *   且本类 `bucketId` 恒为空串、构造时从不赋值，故 `getPublicUrl/createSignedUrl` 等需要拼 `cloud://` 的方法
 *   必须传**完整 fileID**（而非相对 path），否则 `_normalizeCloudId` 抛 `bucketId is not set`。
 *   PG 环境若要按 bucket 语义做原生对象操作，应改用 `app.storage.from(bucketId)`（StorageFileApi）。
 */
export class ClassicStorageFileApi {
  protected shouldThrowOnError = false
  protected bucketId = ''
  protected context?: ICloudbaseContext

  /**
   * 底层传统云存储能力（组合而非继承）。
   *
   * 本类只是把这套底层方法（`uploadFile` / `copyFile` / `getTempFileURL` / `getFileInfo` /
   * `deleteFile` / `getUploadMetadata`）重新包装成 Supabase 风格签名，因此通过**委托**复用，
   * 而不继承——避免把基类的传统 API 一并暴露在本类实例上。
   *
   * 这里刻意标注为 `any`：基类方法均挂 `@catchErrorsDecorator`，该装饰器签名 TS 无法解析，
   * 原先继承时 `this.uploadFile()` 等返回值本就被降级为 `any`，本类的结果处理代码也是按 `any` 编写的。
   * 用 `any` 持有可忠实保留改造前的行为，避免暴露一批本被掩盖的松散类型告警。
   */
  private storage: any

  constructor(context?: ICloudbaseContext, shouldThrowOnError = false) {
    this.context = context
    this.shouldThrowOnError = shouldThrowOnError
    // 注入同一 context，使底层 storage 的 config/request 与本类一致
    this.storage = new CloudbaseStorage(context)
  }

  get config() {
    // @ts-ignore
    return this.context?.config
  }

  get request() {
    // @ts-ignore
    return this.context?.request
  }

  throwOnError(): this {
    this.shouldThrowOnError = true
    return this
  }

  /**
   * 上传文件。
   *
   * 与 Supabase `StorageFileApi.upload(path, fileBody, fileOptions?)` 的差异：
   * - **参数命名**：一致（`path` / `fileBody` / `fileOptions`）。
   * - **默认行为**：本实现默认 `upsert: true`（覆盖），Supabase 默认 `upsert: false`（不覆盖）。
   * - **metadata 传递**：经 CloudBase 私有头 `x-cos-metadata-metadata` 传递，Supabase 走 `x-metadata` / FormData。
   * - **返回值**：`fullPath` 直接等于入参 `path`；Supabase 的 `fullPath` 为 `bucketId/path` 形态。
   */
  async upload(
    path: string,
    fileBody: FileBody,
    fileOptions?: FileOptions,
  ): Promise<
    Result<{ id: string; path: string; fullPath: string }>
    > {
    const options = { upsert: true, ...fileOptions }
    const { cacheControl, contentType, metadata } = options
    try {
      const cloudPath = this.getCloudPath(path)
      const uploadFileParams: Parameters<CloudbaseStorage['uploadFile']>[0] = {
        cloudPath,
        filePath: fileBody as any,
      }

      if (cacheControl || contentType || metadata) {
        const headers: Record<string, string> = {}
        if (cacheControl) {
          headers['cache-control'] = cacheControl
        }
        if (contentType) {
          headers['content-type'] = contentType
        }

        if (metadata) {
          headers['x-cos-metadata-metadata'] = this.toBase64(JSON.stringify(metadata))
        }

        uploadFileParams.headers = headers
      }

      const result = await this.storage.uploadFile(uploadFileParams)

      // IUploadFileRes 没有 code 字段，如果没有 fileID 则表示失败
      if (!result.fileID) {
        throw new Error(JSON.stringify({
          code: ERRORS.OPERATION_FAIL,
          msg: `[${COMPONENT_NAME}.update] no fileID returned`,
        }),)
      }

      return {
        data: {
          id: result.fileID,
          path,
          fullPath: path,
        },
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return {
          data: null,
          error,
        }
      }
      throw error
    }
  }

  /**
   * 使用签名 URL 上传文件。
   *
   * 与 Supabase `StorageFileApi.uploadToSignedUrl(path, token, fileBody, fileOptions?)` 的差异：
   * - **参数命名**：一致，但第二参 `token` 在本实现中被**忽略**（命名为 `_token`）。
   * - **行为**：不走签名 URL 通道，内部直接委托 `upload()`，因此 `token` 无实际作用。
   */
  async uploadToSignedUrl(
    path: string,
    _token: string,
    fileBody: FileBody,
    fileOptions?: FileOptions,
  ): Promise<
    Result<{ id: string; path: string; fullPath: string }>
    > {
    return this.upload(path, fileBody, fileOptions)
  }

  /**
   * 创建签名上传 URL。
   *
   * 与 Supabase `StorageFileApi.createSignedUploadUrl(path, options?: { upsert })` 的差异：
   * - **参数缺失**：**缺第二参 `options`**（不支持 `upsert`）。
   * - **返回值**：在 `{ signedUrl, token, path }` 基础上额外返回 CloudBase 私有字段
   *   `authorization` / `id` / `cosFileId` / `downloadUrl`（供 `uploadToSignedUrl` 等使用）。
   */
  async createSignedUploadUrl(path: string): Promise<
  Result<{
    signedUrl: string
    token: string
    path: string
    // CloudBase 额外的元数据字段
    authorization?: string
    id?: string
    cosFileId?: string
    downloadUrl?: string
  }>
  > {
    try {
      const cloudPath = this.getCloudPath(path)

      const { data: metadata } = await this.storage.getUploadMetadata({ cloudPath })

      return {
        data: {
          signedUrl: metadata.url,
          token: metadata.token,
          path,
          // 返回 CloudBase 的额外元数据，供 uploadToSignedUrl 使用
          authorization: metadata.authorization,
          id: metadata.fileId,
          cosFileId: metadata.cosFileId,
          downloadUrl: metadata.download_url,
        },
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      return {
        data: null,
        error: error instanceof StorageError ? error : new StorageError(error.message),
      }
    }
  }

  /**
   * 更新（覆盖）文件。
   *
   * 与 Supabase `StorageFileApi.update(path, fileBody, fileOptions?)` 的差异：
   * - **参数命名**：一致。
   * - **行为**：强制 `upsert: true` 后委托 `upload()`；Supabase 的 `update` 同样总是覆盖，语义基本一致。
   */
  async update(
    path: string,
    fileBody: FileBody,
    fileOptions?: FileOptions,
  ): Promise<
    Result<{ id: string; path: string; fullPath: string }>
    > {
    return this.upload(path, fileBody, { ...fileOptions, upsert: true })
  }

  /**
   * 移动文件。
   *
   * 与 Supabase `StorageFileApi.move(fromPath, toPath, options?: DestinationOptions)` 的差异：
   * - **参数缺失**：**缺第三参 `options`**（不支持 `destinationBucket` 等，已注释占位）。
   * - **底层实现**：通过 `copyFile(removeOriginal: true)` 模拟移动。
   * - **返回值**：`data.message` 为 `File moved from X to Y`；Supabase 固定返回 `Successfully moved`。
   */
  async move(
    fromPath: string,
    toPath: string,
    // options?: DestinationOptions,
  ): Promise<Result<{ message: string }>> {
    try {
      const result = await this.storage.copyFile({
        fileList: [
          {
            srcPath: this.getCloudPath(fromPath),
            dstPath: this.getCloudPath(toPath),
            overwrite: true,
            removeOriginal: true,
          },
        ],
      })

      if (result.fileList[0].code && result.fileList[0].code !== 'SUCCESS') {
        throw new StorageError(result.fileList[0].message || 'Move failed')
      }

      return {
        data: { message: `File moved from ${fromPath} to ${toPath}` },
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return {
          data: null,
          error,
        }
      }
      throw error
    }
  }

  /**
   * 复制文件。
   *
   * 与 Supabase `StorageFileApi.copy(fromPath, toPath, options?: DestinationOptions)` 的差异：
   * - **参数缺失**：**缺第三参 `options`**（不支持 `destinationBucket` 等，已注释占位）。
   * - **返回值**：`data.path` 为清洗后的目标相对路径；Supabase 返回后端 `data.Key`（`bucketId/path` 形态）。
   */
  async copy(
    fromPath: string,
    toPath: string,
    // options?: DestinationOptions,
  ): Promise<Result<{ path: string }>> {
    try {
      const result = await this.storage.copyFile({
        fileList: [
          {
            srcPath: this.getCloudPath(fromPath),
            dstPath: this.getCloudPath(toPath),
            overwrite: true,
            removeOriginal: false,
          },
        ],
      })

      if (result.fileList[0].code && result.fileList[0].code !== 'SUCCESS') {
        throw new StorageError(result.fileList[0].message || 'Copy failed')
      }

      return {
        data: { path: this.getCloudPath(toPath) },
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return { data: null, error }
      }
      throw error
    }
  }

  /**
   * 创建签名下载 URL。
   *
   * 与 Supabase `StorageFileApi.createSignedUrl(path, expiresIn, options?: { download, transform, cacheNonce })` 的差异：
   * - **参数命名**：一致（`path` / `expiresIn` / `options`）。
   * - **options 缺字段**：仅支持 `download` / `transform`，**缺 `cacheNonce`**。
   * - **底层实现**：基于 `getTempFileURL` 的临时 URL；`transform` 走腾讯云数据万象 imageMogr2，与 Supabase 的转换参数不同。
   */
  async createSignedUrl(
    path: string,
    expiresIn: number,
    options?: {
      download?: string | boolean
      transform?: TransformOptions
    },
  ): Promise<Result<{ signedUrl: string }>> {
    try {
      const cloudPath = this.normalizeCloudId(path)
      const result = await this.storage.getTempFileURL({
        fileList: [
          {
            fileID: cloudPath,
            maxAge: expiresIn,
          },
        ],
      })

      // IGetFileUrlItem 有 code 字段但没有 message 字段
      if (result.fileList[0].code !== 'SUCCESS') {
        throw new StorageError(`Failed to create signed URL: [${result.fileList[0].code}] ${result.fileList[0].fileID}`)
      }

      let signedUrl = result.fileList[0].download_url

      // 构建查询参数
      const queryParams: string[] = []

      // 如果有 download 参数，添加到 URL 中
      if (options?.download !== undefined) {
        if (typeof options.download === 'string') {
          // download 是文件名
          queryParams.push(`download=${encodeURIComponent(options.download)}`)
        } else if (options.download === true) {
          // download 是 true，使用原文件名或默认值
          queryParams.push('download=true')
        }
      }

      // 如果有图片转换参数，添加到 URL 中
      if (options?.transform) {
        const transformQuery = this.transformOptsToQueryString(options.transform)
        if (transformQuery) {
          queryParams.push(transformQuery)
        }
      }

      // 拼接所有查询参数
      if (queryParams.length > 0) {
        const separator = signedUrl.includes('?') ? '&' : '?'
        signedUrl = `${signedUrl}${separator}${queryParams.join('&')}`
      }

      return {
        data: { signedUrl },
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return {
          data: null,
          error,
        }
      }
      throw error
    }
  }

  /**
   * 批量创建签名下载 URL。
   *
   * 与 Supabase `StorageFileApi.createSignedUrls(paths, expiresIn, options?: { download, cacheNonce })` 的差异：
   * - **参数缺失**：**缺第三参 `options`**（不支持 `download` / `cacheNonce`，已注释占位）。
   * - **返回项结构**：`{ path, signedUrl, error }`；Supabase 还会返回原始 `signedURL` 字段。
   */
  async createSignedUrls(
    paths: string[],
    expiresIn: number,
    // options?: {
    //   download?: string | boolean
    // },
  ): Promise<
    Result<Array<{ path: string; signedUrl: string; error: string | null }>>
    > {
    try {
      const fileList = paths.map(p => ({
        fileID: this.normalizeCloudId(p),
        maxAge: expiresIn,
      }))

      const result = await this.storage.getTempFileURL({ fileList })

      return {
        data: result.fileList.map((item: any, index: number) => ({
          path: paths[index],
          signedUrl: item.tempFileURL || '',
          error: item.code === 'SUCCESS' ? null : item.message,
        })),
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return {
          data: null,
          error,
        }
      }
      throw error
    }
  }

  /**
   * 下载文件，返回 Blob。
   *
   * 与 Supabase `StorageFileApi.download(path, options?: { transform, cacheNonce }, parameters?)` 的差异：
   * - **第二参类型**：本实现 `options` 直接是 `TransformOptions`，Supabase 是包裹形态 `{ transform, cacheNonce }`。
   * - **参数缺失**：缺 `cacheNonce`、以及第三参 `parameters`（fetch 选项 / AbortSignal）。
   * - **返回值**：返回 `Promise<{ data: Blob; error }>`；Supabase 返回 `BlobDownloadBuilder`（可链式 `throwOnError`）。
   */
  async download(
    path: string,
    options?: TransformOptions,
  ): Promise<{
    data: Blob | null
    error: StorageError | null
  }> {
    try {
      return {
        data: await (async () => {
          const signedUrlResult = await this.createSignedUrl(path, 600, { transform: options })
          if (signedUrlResult.error) {
            throw signedUrlResult.error
          }
          const tmpUrl = encodeURI(signedUrlResult.data?.signedUrl)

          const { data } = await (this as any).request.reqClass.get({
            url: tmpUrl,
            headers: {}, // 下载资源请求不经过service，header清空
            responseType: 'blob',
          })

          if (!data) {
            throw new StorageError('Download failed: no file content')
          }

          // 将 Buffer 转换为 Uint8Array 以兼容 Blob
          return new Blob([data])
        })(),
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return {
          data: null,
          error,
        }
      }
      throw error
    }
  }

  /**
   * 获取文件信息
   *
   * 与 Supabase `StorageFileApi.info(path)` 的差异：
   * - **参数命名**：本实现命名为 `pathOrFileId`（Supabase 为 `path`），且额外支持传入 `cloud://` 完整 fileID。
   * - **返回值**：同为 `Camelize<FileObjectV2>`，但 `metadata` 恒为 `{}`、`version` 恒为 `'1'`，部分时间字段为近似值。
   *
   * @param pathOrFileId - 相对路径（如 'images/photo.jpg'）或 CloudBase fileID（以 'cloud://' 开头）
   * @returns 文件信息对象
   *
   * @example
   * ```typescript
   * // 使用相对路径
   * const { data } = await bucket.info('images/photo.jpg')
   *
   * // 使用 CloudBase fileID
   * const { data } = await bucket.info('cloud://env-id.xxxx-xxxx/images/photo.jpg')
   * ```
   */
  async info(pathOrFileId: string,): Promise<Result<Camelize<FileObjectV2>>> {
    try {
      // 判断是 fileID 还是相对路径
      const isFileId = pathOrFileId.startsWith('cloud://')
      const displayName = isFileId ? this.extractPathFromFileId(pathOrFileId) : pathOrFileId
      const bucketId = isFileId ? this.extractBucketFromFileId(pathOrFileId) : this.bucketId

      const fileInfo = await this.storage.getFileInfo({
        fileList: [this.normalizeCloudId(pathOrFileId)],
      })

      const item = fileInfo.fileList[0]

      if (item.code !== 'SUCCESS') {
        throw new StorageError(item.message)
      }

      const now = new Date().toISOString()
      const lastModified = (item.lastModified ? new Date(item.lastModified) : new Date()).toISOString()
      return {
        data: {
          id: item.fileID,
          version: '1',
          name: displayName,
          bucketId,
          updatedAt: lastModified,
          createdAt: lastModified,
          lastAccessedAt: now,
          size: item.size,
          cacheControl: item.cacheControl,
          contentType: item.contentType,
          etag: item.etag,
          lastModified,
          metadata: {},
        },
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      return {
        data: null,
        error: error instanceof StorageError ? error : new StorageError(error.message),
      }
    }
  }

  /**
   * 判断文件是否存在。
   *
   * 与 Supabase `StorageFileApi.exists(path)` 的差异：
   * - **参数命名**：本实现命名为 `pathOrFileId`（Supabase 为 `path`），且额外支持 `cloud://` fileID。
   * - **底层实现**：基于 `getFileInfo`，将 `FILE_NOT_FOUND` 视为不存在；Supabase 用 HEAD 请求 + 400/404 判定。
   */
  async exists(pathOrFileId: string): Promise<Result<boolean>> {
    try {
      // 判断是 fileID 还是相对路径
      const fileInfo = await this.storage.getFileInfo({
        fileList: [this.normalizeCloudId(pathOrFileId)],
      })

      const item = fileInfo.fileList[0]

      if (item.code === 'FILE_NOT_FOUND') {
        return {
          data: false,
          error: null,
        }
      }

      if (item.code !== 'SUCCESS') {
        throw new StorageError(item.message)
      }

      return { data: true, error: null }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return {
          data: null,
          error,
        }
      }
      throw error
    }
  }

  /**
   * 获取公开访问 URL。
   *
   * 与 Supabase `StorageFileApi.getPublicUrl(path, options?)` 的差异：
   * - **同步性**：Supabase 为**同步**方法直接返回 `{ data: { publicUrl } }`；本实现为 **`async`**（返回 Promise），调用方式不兼容。
   * - **底层实现**：实际返回的是 600s 的签名 URL（基于 `createSignedUrl`），而非真正的永久公开直链。
   * - **options 缺字段**：支持 `download` / `transform`，缺 `cacheNonce`。
   * - **返回值**：失败时返回 `{ data: null, error }`；Supabase 不返回 error。
   */
  async getPublicUrl(
    path: string,
    options?: {
      download?: string | boolean
      transform?: TransformOptions
    },
  ): Promise<
    | {
      data: { publicUrl: string }
    }
    | { data: null; error: StorageError }
    > {
    const res = await this.createSignedUrl(path, 600, options)

    if (res.data) {
      return {
        data: { publicUrl: res.data.signedUrl },
      }
    }

    return { data: null, error: res.error as StorageError }
  }

  /**
   * 批量删除文件。
   *
   * 与 Supabase `StorageFileApi.remove(paths)` 的差异：
   * - **参数命名**：一致（`paths: string[]`）。
   * - **返回值**：返回的 `FileObject[]` 仍包含 Supabase 新版已不再返回的废弃字段（`bucket_id` / `owner` / `buckets`），
   *   且这些字段多为占位/未知值。
   * - **实现开销**：删除前会先分批（每组 10 个）拉取文件信息以补全返回结构。
   */
  async remove(paths: string[]): Promise<Result<FileObject[]>> {
    try {
      // 分组获取文件信息，每组最多10个
      const chunkSize = 10
      const pathChunks: string[][] = []
      for (let i = 0; i < paths.length; i += chunkSize) {
        pathChunks.push(paths.slice(i, i + chunkSize))
      }

      // 并行获取所有分组的文件信息
      const fileInfoResults = await Promise.all(pathChunks.map(chunk => Promise.all(chunk.map(path => this.info(path)))),)

      // 合并所有文件信息并构建映射表（path -> fileInfo）
      const fileInfoMap = new Map<string, Camelize<FileObjectV2>>()
      fileInfoResults.forEach((chunkResults, chunkIndex) => {
        chunkResults.forEach((result, index) => {
          if (result.data) {
            fileInfoMap.set(paths[chunkIndex * chunkSize + index], result.data)
          }
        })
      })

      // 执行删除操作
      const fileList = paths.map(p => this.normalizeCloudId(p))
      const result: { fileList: Array<{ code?: string }> } = await this.storage.deleteFile({ fileList })

      // IDeleteFileRes 的 fileList 数组中每个项有 code 字段
      const failedFiles = result.fileList.filter(item => item.code !== 'SUCCESS')
      if (failedFiles.length > 0) {
        throw new StorageError(`Delete failed for ${failedFiles.length} file(s)`)
      }

      const now = new Date().toISOString()

      // 使用获取到的文件信息构建返回数据
      return {
        data: paths.map((p) => {
          const info = fileInfoMap.get(p)
          return {
            name: info?.name,
            id: info?.id,
            bucket_id: info?.bucketId,
            owner: undefined, // 无法计算owner
            updated_at: info?.updatedAt || now,
            created_at: info?.createdAt,
            last_accessed_at: info?.lastAccessedAt || now,
            metadata: info?.metadata || {},
            /**
             * TODO: 获取补全 Bucket 信息
             */
            buckets: {
              id: info?.bucketId,
              name: info?.bucketId,
              owner: undefined, // 无法计算owner
              public: false, // 未知
              created_at: '', // 未知
              updated_at: now, // 未知
            },
          } as unknown as FileObject
        }),
        error: null,
      }
    } catch (error: any) {
      if (this.shouldThrowOnError) throw error
      if (isStorageError(error)) {
        return {
          data: null,
          error,
        }
      }
      throw error
    }
  }

  /**
   * 列举文件。
   *
   * 与 Supabase `StorageFileApi.list(path?, options?: SearchOptions, parameters?)` 的差异：
   * - **未实现**：本实现直接抛 `Not implemented`，且**无任何参数**；Supabase 支持前缀路径、分页/排序选项与 fetch 参数。
   */
  async list() {
    throw new StorageError('Not implemented')
  }

  private getCloudPath(path: string): string {
    // 清理路径：移除首尾斜杠，合并多个斜杠
    const cleanPath = path.replace(/^\/|\/$/g, '').replace(/\/+/g, '/')

    // 否则返回清理后的相对路径
    return cleanPath
  }

  private normalizeCloudId(path: string) {
    // 如果已经是 cloud:// 格式，直接返回
    if (/^cloud:\/\//.test(path)) {
      return path
    }
    const cleanPath = this.getCloudPath(path)

    // 如果设置了 bucketId，构建完整的 cloud:// 路径
    if (this.bucketId) {
      // 获取环境 ID
      const envId = this.config?.env || ''
      if (envId) {
        return `cloud://${envId}.${this.bucketId}/${cleanPath}`
      }
    } else {
      throw new StorageError('bucketId is not set')
    }
  }

  private toBase64(data: string) {
    return toBase64(data)
  }

  /**
   * 将 TransformOptions 转换为腾讯云数据万象的 imageMogr2 查询字符串
   *
   * 腾讯云数据万象使用 imageMogr2 接口进行图片处理，支持以下参数：
   * - /thumbnail/<Width>x<Height> - 缩放
   * - /format/<Format> - 格式转换
   * - /quality/<Quality> - 质量调整
   * - /rquality/<Quality> - 相对质量
   *
   * @param transform - Supabase TransformOptions
   * @returns 腾讯云数据万象的查询字符串
   *
   * @example
   * ```typescript
   * transformOptsToQueryString({ width: 300, height: 200, quality: 80, format: 'origin' })
   * // 返回: 'imageMogr2/thumbnail/300x200/quality/80'
   * ```
   */
  private transformOptsToQueryString(transform: TransformOptions): string {
    const params: string[] = ['imageMogr2']

    // 处理缩放参数
    if (transform.width || transform.height) {
      const width = transform.width || ''
      const height = transform.height || ''

      // 根据 resize 模式选择不同的缩放方式
      if (transform.resize === 'fill') {
        // fill: 强制缩放到指定尺寸，可能变形
        params.push(`thumbnail/${width}x${height}!`)
      } else if (transform.resize === 'contain') {
        // contain: 等比缩放，完整显示在指定尺寸内
        params.push(`thumbnail/${width}x${height}`)
      } else {
        // cover (默认): 等比缩放，填充指定尺寸，可能裁剪
        // 使用 /thumbnail/<Width>x<Height>^ 表示填充模式
        params.push(`thumbnail/${width}x${height}^`)
      }
    }

    // 处理格式转换
    // format: 'origin' 表示保持原格式，不传此参数
    if (transform.format && transform.format !== 'origin') {
      params.push(`format/${transform.format}`)
    }

    // 处理质量参数
    if (transform.quality !== undefined) {
      // 腾讯云数据万象的 quality 参数范围是 1-100
      const quality = Math.max(1, Math.min(100, transform.quality))
      params.push(`quality/${quality}`)
    }

    return params.join('/')
  }

  /**
   * 从 CloudBase fileID 中提取文件路径
   *
   * @param fileId - CloudBase fileID (格式: cloud://env-id.bucket/path/to/file.jpg)
   * @returns 提取的文件路径
   *
   * @example
   * ```typescript
   * extractPathFromFileId('cloud://test-env.test-bucket/images/photo.jpg')
   * // 返回: 'images/photo.jpg'
   *
   * extractPathFromFileId('cloud://test-env.test-bucket/file.txt')
   * // 返回: 'file.txt'
   * ```
   */
  private extractPathFromFileId(fileId: string): string {
    // fileID 格式: cloud://env-id.bucket/path/to/file.jpg
    // 需要提取第一个 / 后面的所有内容（包括 bucket 和路径）
    // 然后再提取 bucket 后面的路径部分

    // 移除 cloud:// 前缀
    const withoutProtocol = fileId.replace(/^cloud:\/\//, '')

    // 分割为 [env-id.bucket, path, to, file.jpg]
    const parts = withoutProtocol.split('/')

    if (parts.length < 2) {
      // 如果无法解析，返回完整的 fileID
      return fileId
    }

    // 移除第一部分（env-id.bucket），返回剩余路径
    return parts.slice(1).join('/')
  }

  /**
   * 从 CloudBase fileID 中提取 bucket 名称
   *
   * @param fileId - CloudBase fileID (格式: cloud://env-id.bucket/path/to/file.jpg)
   * @returns 提取的 bucket 名称
   *
   * @example
   * ```typescript
   * extractBucketFromFileId('cloud://test-env.test-bucket/images/photo.jpg')
   * // 返回: 'test-bucket'
   *
   * extractBucketFromFileId('cloud://prod-env.my-bucket/file.txt')
   * // 返回: 'my-bucket'
   * ```
   */
  private extractBucketFromFileId(fileId: string): string {
    // fileID 格式: cloud://env-id.bucket/path/to/file.jpg
    // 需要提取 env-id.bucket 中的 bucket 部分

    // 移除 cloud:// 前缀
    const withoutProtocol = fileId.replace(/^cloud:\/\//, '')

    // 分割为 [env-id.bucket, path, to, file.jpg]
    const parts = withoutProtocol.split('/')

    if (parts.length < 1) {
      // 如果无法解析，返回默认 bucket
      return ''
    }

    // 第一部分是 env-id.bucket，提取 bucket 部分
    // 格式: env-id.bucket 或 env-id.bucket-name
    const envAndBucket = parts[0]

    // 查找第一个点的位置
    const dotIndex = envAndBucket.indexOf('.')

    if (dotIndex === -1) {
      // 如果没有点，返回整个字符串
      return envAndBucket
    }

    // 返回点后面的部分（bucket 名称）
    return envAndBucket.substring(dotIndex + 1)
  }
}
